"use client"

import { useId, useRef, useState } from "react"

/**
 * The in-card signup form for a `DarkCallout` newsletter card (FRIDGE-357).
 *
 * The article sidebar's "Newsletter — Subscribe →" card used to be a link to
 * /contact, so a reader who pressed Subscribe was sent to a contact form and
 * no address was ever captured. This form posts the address through the same
 * machinery as the footer band and the capture popup — POST
 * /api/alerts/subscribe, type `newsletter` — so it lands in
 * `newsletter_subscriptions` with `source` naming the surface. Honeypot-guarded
 * like every other signup form.
 */

type Status = "idle" | "submitting" | "success" | "error"

/** The exact body this form posts. Exported so the capture test can run it
 *  through the real handler. */
export function newsletterCalloutPayload(email: string, source: string, honeypot: string) {
  return {
    type: "newsletter",
    email: email.trim(),
    source,
    generalUpdatesOptIn: true,
    honeypot,
  }
}

export function NewsletterCalloutForm({
  source,
  cta,
  buttonBg,
  buttonColor,
  buttonWeight,
}: {
  source: string
  cta: string
  buttonBg: string
  buttonColor: string
  buttonWeight: number
}) {
  const [status, setStatus] = useState<Status>("idle")
  const [email, setEmail] = useState("")
  const honeypotRef = useRef("")
  const inputId = useId()

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (honeypotRef.current) return
    setStatus("submitting")
    try {
      const res = await fetch("/api/alerts/subscribe", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(newsletterCalloutPayload(email, source, honeypotRef.current)),
      })
      const data = await res.json().catch(() => null)
      setStatus(res.ok && data?.ok ? "success" : "error")
    } catch {
      setStatus("error")
    }
  }

  if (status === "success") {
    return (
      <p
        data-state="success"
        role="status"
        style={{ fontSize: 14, fontWeight: 700, margin: 0, position: "relative" }}
      >
        Thanks, you&apos;re on the list.
      </p>
    )
  }

  return (
    <form onSubmit={onSubmit} style={{ position: "relative" }}>
      {/* Honeypot: hidden field real users never fill. */}
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        className="hidden"
        onChange={(e) => {
          honeypotRef.current = e.target.value
        }}
      />
      {/* The input sits inside its label (and keeps htmlFor), so both jsx-a11y
          label rules see the association — the ratchet in
          scripts/accessibility-baseline.json may only fall. */}
      <label htmlFor={inputId} style={{ display: "block" }}>
        <span id={`${inputId}-label`} className="sr-only">Email address</span>
        <input
          id={inputId}
          aria-labelledby={`${inputId}-label`}
          type="email"
          required
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="you@email.com"
          autoComplete="email"
          disabled={status === "submitting"}
          style={{
            display: "block",
            width: "100%",
            boxSizing: "border-box",
            background: "#fff",
            color: "var(--gray-900)",
            border: "none",
            padding: "11px 14px",
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 8,
          }}
        />
      </label>
      <button
        type="submit"
        disabled={status === "submitting"}
        style={{
          display: "block",
          width: "100%",
          background: buttonBg,
          color: buttonColor,
          padding: "11px 14px",
          borderRadius: 8,
          border: "none",
          fontSize: 13,
          fontWeight: buttonWeight,
          textAlign: "center",
          cursor: status === "submitting" ? "not-allowed" : "pointer",
        }}
      >
        {status === "submitting" ? "Sending…" : cta}
      </button>
      {status === "error" && (
        <p role="alert" style={{ fontSize: 12, margin: "8px 0 0", color: "#fff" }}>
          Something went wrong. Please check your email and try again.
        </p>
      )}
    </form>
  )
}
